import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Pagination, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { usePaymentAccounts, usePaymentLinks } from "../../../api/queries/payment/paymentQueries";
import APP_ROUTES from "../../../routers/routeConstants";

import PaymentHeader from "../components/PaymentHeader";
import LearnMorePopUp from "../components/LearnMorePopUp";
import PaymentLinkRow from "./PaymentLinkRow";
import PaymentLinksControl from "./PaymentLinksControl";
import { PaymentLink } from "../payment.types";
import EndPaymentLinkPopUp from "./EndPaymentLinkPopUp";
import NoPaymentLinksFigure from "./NoPaymentLinksFigure";
import { useDeletePaymentLink } from "../../../api/queries/payment/paymentMutations";
import { useDebounce } from "../../../hooks/useDebounce";

const CreatePaymentLinks = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(0);
  const [isLearnMorePopUpOpen, setIsLearnMorePopUpOpen] = useState(false);
  const [isEndPaymentLinkPopUpOpen, setIsEndPaymentLinkPopUpOpen] = useState(false);
  const [targetPaymentLink, setTargetPaymentLink] = useState<PaymentLink | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [statusValue, setStatusValue] = useState("all");
  const debouncedSearchValue = useDebounce(searchValue, 300);

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

  const handleChangeText = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchValue(e.target.value);
  };

  const openLearnMorePopUp = () => setIsLearnMorePopUpOpen(true);
  const closeLearnMorePopUp = () => setIsLearnMorePopUpOpen(false);

  const closeEndPaymentLinkPopUp = () => {
    setIsEndPaymentLinkPopUpOpen(false);
    setTargetPaymentLink(null);
  };

  const handleSetupPaymentAccount = () => navigate(APP_ROUTES.CREATE_PAYMENT_ACCOUNT);

  const { userPaymentAccount, isUserPaymentAccountLoading, isUserPaymentAccountHasError } = usePaymentAccounts();
  const { userPaymentLinks, isUserPaymentLinksLoading, isUserPaymentLinksHasError } = usePaymentLinks({
    page,
    searchValue: debouncedSearchValue,
    dateRange,
    status: statusValue,
  });

  const handleOnSharePaymentLink = (Link: PaymentLink) => {};

  const handleOnEndPaymentLink = (link: PaymentLink) => {
    setIsEndPaymentLinkPopUpOpen(true);
    setTargetPaymentLink(link);
  };

  const { mutate: deletePaymentLinkMutation } = useDeletePaymentLink();

  const endPaymentLinkHandler = () => {
    if (targetPaymentLink) {
      const linkId = Number(targetPaymentLink.id);
      deletePaymentLinkMutation(linkId);
    }
    closeEndPaymentLinkPopUp();
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value - 1);
  };

  const disableCanCreatePaymentLink =
    isUserPaymentAccountLoading || !userPaymentAccount || isUserPaymentAccountHasError;
  return (
    <Box
      sx={{
        padding: "12px",
        width: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        rowGap: "24px",
      }}
    >
      <PaymentHeader
        openLearnMorePopUp={openLearnMorePopUp}
        handleSetupPaymentAccount={handleSetupPaymentAccount}
        // canCreatePaymentLink={!!userPaymentAccount}
        disableCanCreatePaymentLink={disableCanCreatePaymentLink}
      />
      <PaymentLinksControl
        searchValue={searchValue}
        dateRange={dateRange}
        setDateRange={setDateRange}
        handleChangeText={handleChangeText}
        statusValue={statusValue}
        setStatusValue={setStatusValue}
      />
      <Box
        sx={{
          padding: "24px ",
          borderRadius: "12px",
          background: "var(--Base-White, #FFF)",
        }}
      >
        <Typography
          sx={{
            color: "var(--Gray-900, #101828)",
            fontFamily: "Montserrat",
            fontSize: "20px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "30px",
            marginBottom: "32px",
          }}
        >
          {t("pages.payment.paymentLinks.paymentLinks")}
        </Typography>
        {!isUserPaymentLinksLoading &&
          userPaymentLinks &&
          userPaymentLinks?.items?.length > 0 &&
          userPaymentLinks.items.map((Link, idx) => (
            <PaymentLinkRow
              key={idx}
              Link={Link}
              isLast={userPaymentLinks.items.length - 1 === idx}
              handleOnSharePaymentLink={handleOnSharePaymentLink}
              handleOnEndPaymentLink={handleOnEndPaymentLink}
            />
          ))}
        {isUserPaymentLinksHasError && (
          <NoPaymentLinksFigure disableCanCreatePaymentLink={disableCanCreatePaymentLink} />
        )}
        {!isUserPaymentLinksLoading && userPaymentLinks?.items?.length === 0 && (
          <NoPaymentLinksFigure disableCanCreatePaymentLink={disableCanCreatePaymentLink} />
        )}
      </Box>
      {!isUserPaymentLinksLoading && userPaymentLinks && userPaymentLinks?.items?.length > 0 && (
        <Pagination
          count={userPaymentLinks?.meta?.totalPages}
          page={!userPaymentLinks?.meta?.totalItems || userPaymentLinks?.meta?.totalItems <= 0 ? 1 : page + 1}
          onChange={handleChangePage}
          sx={{
            margin: "33px auto 0",
            direction: "ltr !important",
            "& .MuiPagination-ul": {
              justifyContent: "center",
            },
            "& .MuiButtonBase-root": {
              borderRadius: "8px !important",
            },
          }}
        />
      )}
      <LearnMorePopUp onClose={closeLearnMorePopUp} open={isLearnMorePopUpOpen} />
      <EndPaymentLinkPopUp
        onClose={closeEndPaymentLinkPopUp}
        open={isEndPaymentLinkPopUpOpen}
        endPaymentLinkHandler={endPaymentLinkHandler}
      />
    </Box>
  );
};

export default CreatePaymentLinks;
